import { useEffect, useState } from 'react';
import { Box, Button, Card, Modal, Stack } from '@mui/material';
import MuitextField from '@mui/material/TextField';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { PropTypes } from 'prop-types';
import { notification } from 'antd';
import Phone from '../../layouts/Phone';
import Desktop from '../../layouts/Desktop';
import instance from '../../api/axiosRequest';
import ConfirmDeleteModal from '../ConfirmDeleteModal';

const TextField = (props) => <MuitextField fullWidth sx={{ mb: 2 }} {...props} />;

const openNotification = ({ message, description = '', type }) => {
  notification.open({
    type,
    message,
    description,
    duration: 4,
  });
};

const BtnTypography = (props) => (
  <Button
    variant="outlined"
    sx={{
      p: 1,
      border: '1px solid gray',
      mb: 2,
      ...props.styles,
    }}
    {...props}
  />
);

BtnTypography.propTypes = {
  styles: PropTypes.object,
};

export default function CreateEmailTemplate({ templateName }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState('mobile');
  const [open, setOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const formik = useFormik({
    initialValues: {
      templateName: '',
      subjectPart: '',
      htmlPart: '',
      textPart: '',
    },
    validationSchema: Yup.object().shape({
      templateName: Yup.string().matches(
        /^[a-z0-9-_]+$/i,
        "Only alphanumeric ASCII characters, '_', and '-' are allowed."
      ),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        if (templateName) await instance.put('/mail/updateTemplate', values);
        else await instance.post('/mail/createTemplate', values);
        openNotification({
          message: 'Operation Successful.',
          type: 'success',
        });
        navigate('/dashboard/campaigns/email-templates/new');
        setLoading(false);
      } catch (error) {
        setLoading(false);
        let description = '';
        if (error.response.data) description = error.response.data.message;
        openNotification({
          message: 'Operation Failed.',
          description,
          type: 'error',
        });
        console.log(error);
      }
    },
  });

  useEffect(() => {
    const getTemplateDetails = async () => {
      try {
        const { data } = await instance.post('/mail/templateDetails', { templateName });
        const template = data.data.Template;
        formik.setValues({
          templateName: template.TemplateName || '',
          subjectPart: template.SubjectPart || '',
          htmlPart: template.HtmlPart || '',
          textPart: template.TextPart || '',
        });
      } catch (error) {
        console.log(error);
      }
    };
    if (templateName) getTemplateDetails();
  }, [templateName]);

  const handleDelete = () => setDeleteModalOpen(true);

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <Card sx={{ p: 2, mt: 2 }}>
          <TextField
            name="templateName"
            label="Name"
            value={formik.values.templateName}
            placeholder="Template Name"
            onChange={formik.handleChange}
            required
            error={formik.errors.templateName && formik.touched.templateName}
            helperText={formik.errors.templateName || ''}
          />
          <TextField
            name="subjectPart"
            label="Subject"
            value={formik.values.subjectPart}
            onChange={formik.handleChange}
            required
          />
          <TextField
            name="htmlPart"
            label="HTML"
            required
            value={formik.values.htmlPart}
            onChange={formik.handleChange}
            multiline
            rows={15}
          />
          <TextField
            name="textPart"
            label="Text"
            value={formik.values.textPart}
            placeholder="Template Text"
            onChange={formik.handleChange}
            multiline
            rows={5}
          />
          <Stack direction="row" justifyContent="space-between">
            {templateName && (
              <Button variant="contained" color="error" onClick={handleDelete} sx={{ mr: 2 }}>
                Delete
              </Button>
            )}
            <div>
              <Button variant="contained" onClick={handleOpen} sx={{ mr: 2 }}>
                Preview
              </Button>
              <LoadingButton loading={loading} type="submit" variant="contained">
                {templateName ? 'Update' : 'Save'}
              </LoadingButton>
            </div>
          </Stack>
        </Card>
      </form>
      <ConfirmDeleteModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        path="/mail/deleteTemplate"
        item="Template"
        data={{ templateName }}
        setReload={() => navigate('/dashboard/campaigns/email-templates/new')}
      />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            width: '80%',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'white',
            paddingBlock: 2,
          }}
        >
          <Stack direction="row">
            <BtnTypography
              styles={{ borderRadius: '4px 0px 0px 4px' }}
              variant="body"
              onClick={() => setSelected('mobile')}
            >
              Mobile
            </BtnTypography>
            <BtnTypography
              styles={{ borderRadius: '0px 4px 4px 0px' }}
              variant="body"
              onClick={() => setSelected('desktop')}
            >
              Desktop
            </BtnTypography>
          </Stack>
          {selected === 'mobile' ? <Phone text={formik.values.htmlPart} /> : <Desktop text={formik.values.htmlPart} />}
        </Box>
      </Modal>
    </div>
  );
}
